import React, { createContext, useContext, useMemo } from 'react';
import { flow, makeAutoObservable } from 'mobx';
import { ProductType } from './accessExpress.types';
import { useQuery } from 'react-query';
import { v4 as uuid } from 'uuid';
import {
  addOrderItems,
  clearAllOrderItems,
  createOrderItem,
  deleteOrderItem,
  getLastDraftOrder,
  getOrderDetails,
  updateOrder,
  updateOrderItem,
} from 'api/accessexpressv3/api';
import {
  CreateOrderItem,
  DraftOrderAPIResponse,
  PostDraftRequest,
} from 'api/accessexpressv3/schemas/OrdersSchemas';
import { ProductType as AccessProductType } from 'screens/SelectServices/schemas';
import showToast from 'components/Toast/Toast';
import { toast } from 'react-toastify';
import { QuoteLocation } from 'api/accessexpress/schema';
import { ConfigRequirements } from './BaseComponents/Cart/locationFlow/access/ConfigRequirementsModal/schema';
import { useParams } from 'react-router-dom';
import {
  buildItemPayload,
  filterItemsByCategory,
  getAccessProductName,
  getMNSPrice,
  getOfferingPrice,
} from './utils';
import axios from 'axios';

export type ItemDetails = Partial<AccessProductType> & {
  offering_id?: string;
  isOtherOption?: boolean;
  provider?: string;
  bundle?: string;
  image?: string | null;
  [key: string]: unknown;
};

export interface ICartItem {
  id?: string;
  category: string;
  subCategory?: string;
  type?: string;
  subtype: string;
  name: string;
  price: number;
  quantity: number;
  outOfStock?: boolean;
  locationId?: string | number | null;
  item_details?: ItemDetails;
  cw_ticket_id?: string;
  sf_id?: string;
  cornerstone_id?: string;
  status?: string;
  location_id?: string | null;
}

// export type AddressType = {
//   address_line_1?: string;
//   address_line_2?: string;
//   street?: string;
//   city: string;
//   state: string;
//   zip: string;
//   cw_site_id?: string;
//   name?: string;
//   site_name?: string;
//   macnum?: string;
//   parent_macnum?: string;
//   parent_name?: string;
//   local_contact_name?: string;
//   local_contact_phone?: string;
//   location_contact_email?: string;
//   shipping_method?: string;
//   business_name?: string;
//   location_name?: string;
//   billing_type?: number;
//   invoice_delivery_method?: number;
//   invoice_style?: string;
// };

// export type OrderItemType = {
//   type: string; //  # "access_service" or "mobility_device"
//   subtype: string; //  # "Broadband", "DIA", "Phone", etc.
//   pim_price_id?: string;
//   quantity: number;
//   location_id: string;
//   item_details: { [key: string]: string };
//   price?: number;
// };

// export type LocationType = {
//   address?: AddressType;
//   order_items?: OrderItemType[];
//   id?: string;
// };

// export type OrderType = {
//   id: string;
//   locations: LocationType[];
//   additional_details: {
//     bb_quote_request: string;
//     dia_quote_request: string;
//   };
//   order_name?: string;
//   // total_amount: number;
// };

export interface CartSiteAddress extends Omit<QuoteLocation, 'id'> {
  // Address ID
  id?: string | number;
  // Location ID
  external_id?: string;
  // only for checkout access!!!
  site_name?: string | null;
}

export type ResumeOrderType = {
  id?: string;
  orderName?: string | null;
};

type ShippingAssignmentMethod = 'checkout' | 'manual' | 'unset';

let cancelTokenSource = axios.CancelToken.source();

export class CartStore {
  private _isOpen = false;
  private _isEditingLocations = false;
  private _locations: Array<CartSiteAddress> = [];
  private _stagedLocations: Array<CartSiteAddress> = [];
  // this property is used to define what cart should be shown
  // default - cart with items or empty cart
  // locations - cart with locations form when click access product
  private _cartState: 'default' | 'locations' = 'default';
  loadingDraftQuote: boolean = false;
  loadingDraftForPricing: boolean = true;

  draftId: string | null = null;
  additionalDetails: DraftOrderAPIResponse['additional_details'] = null;
  locationActiveId?: string | number | null = null;
  items: ICartItem[] = [];
  selectedAccessProductCategory: ProductType['category'] | null = null;
  deletedItems: ICartItem[] = [];
  diaFlowActive: boolean = false;
  startPolling: boolean = false;

  priceChangeMap: Record<string, boolean> = {};

  resumeOrder?: ResumeOrderType = undefined;
  showOptimizationModal = false;
  shippingAssignmentMethod: ShippingAssignmentMethod = 'unset';
  unassginedItemsAreAssigned = false;

  constructor() {
    makeAutoObservable(this);
  }

  init() {
    console.log('Cart Provider initialized');
  }

  get locations() {
    const locations = this._isEditingLocations
      ? this._stagedLocations
      : this._locations;

    return locations;
  }

  get totalItems() {
    return this.items.filter((item) => !item.item_details?.parentId).length;
  }

  get totalQuantity() {
    const mobiltyQuantity = this.items.reduce(
      (sum, item) =>
        sum +
        (item.category === 'Mobility' && !item.item_details?.parentId
          ? item.quantity
          : 0),
      0,
    );
    const accessQuantity = this.items.reduce(
      (sum, item) => sum + (item.category === 'Access' ? item.quantity : 0),
      0,
    );
    const totalQuantity = mobiltyQuantity + accessQuantity;
    return totalQuantity;
  }

  get isOpen() {
    return this._isOpen;
  }

  get cartState() {
    return this._cartState;
  }

  get isEditingLocations() {
    return this._isEditingLocations;
  }

  get activeLocation() {
    return this._locations.find((l) => l?.id === this.locationActiveId) ?? null;
  }

  get subTotal() {
    const total = this.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    return total.toFixed(2);
  }

  get total() {
    const total = this.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    return total.toFixed(2);
  }

  get locationMissingServices() {
    const _locations: Array<CartSiteAddress> = [];
    this._locations.forEach((location) => {
      if (!this.items.some((item) => item.locationId === location.id))
        _locations.push(location);
    });
    return _locations;
  }

  get unnassignedItems() {
    return this.items.filter((item) => !item.locationId);
  }

  get unassignedPrimaryMobilityItems() {
    return this.items.filter(
      (item) =>
        item.category === 'Mobility' &&
        !item.locationId &&
        !item.item_details?.parentId,
    );
  }

  get assignedPrimaryItems() {
    return this.items.filter(
      (item) => item.locationId && !item.item_details?.parentId,
    );
  }

  unassignedPrimaryMobilityItemsTotal() {
    return this.unassignedPrimaryMobilityItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
  }

  async addUnassignedMobilityItems(locationId: ICartItem['locationId']) {
    this.unassginedItemsAreAssigned = true;

    const getPimProductPricingId = (item: ICartItem) =>
      item.item_details?.pim_product_pricing_id ??
      item.item_details?.pimProductPricingId;

    const checkSameItemPrimaryAndSecondary = (item: ICartItem) => {
      const existingPrimaryItem = this.items.find(
        (i) =>
          getPimProductPricingId(i) === getPimProductPricingId(item) &&
          i.locationId === locationId,
      );
      if (existingPrimaryItem) {
        existingPrimaryItem.quantity += item.quantity;
        const secondaryItems = this.items.filter(
          (i) =>
            i.item_details?.parentId === item.item_details?.referenceId &&
            i.locationId === locationId,
        );
        if (secondaryItems.length > 0) {
          secondaryItems.forEach((secondaryItem) => {
            secondaryItem.quantity += item.quantity;
          });
        }
        return true;
      }
    };

    const allClonedUnassignedItems: ICartItem[] =
      this.unassignedPrimaryMobilityItems.flatMap((primaryItem) => {
        const newRefId = uuid();
        const refId = primaryItem.item_details?.referenceId;

        if (checkSameItemPrimaryAndSecondary(primaryItem)) {
          return [];
        }

        return [
          {
            ...primaryItem,
            id: undefined,
            locationId,
            referenceId: newRefId,
          },
          ...this.items
            .filter((item) => item.item_details?.parentId === refId)
            .map((item) => ({
              ...item,
              id: undefined,
              locationId,
              parentId: newRefId,
            })),
        ];
      });

    this.items = [...this.items, ...allClonedUnassignedItems];

    if (cancelTokenSource) {
      cancelTokenSource?.cancel('Operation canceled due to new request.');
    }
    cancelTokenSource = axios.CancelToken.source();
    const dataForPostOrUpdate = {
      additional_details: this.additionalDetails ?? {},
      locations: this.locations.map((location) => {
        const order_items = this.itemsByLocation(location.id).map(
          buildItemPayload,
        );
        return {
          address: {
            ...location,
            id: location.id as string,
          },
          order_items,
          id: location.id as string,
        };
      }),
    };
    const draft = await updateOrder(dataForPostOrUpdate, this.draftId!, {
      cancelToken: cancelTokenSource.token,
    });
    // 2. await endpoint to return order update locations
    // 3. store order locations on state;
    this.updateStateFromDraft(draft);

    return allClonedUnassignedItems;
  }

  updateResumeOrder = (order?: ResumeOrderType) => {
    this.resumeOrder = {
      ...this.resumeOrder,
      ...order,
    };
  };
  clearResumeOrder = () => {
    this.resumeOrder = undefined;
  };

  setCartState = (state: 'default' | 'locations') => {
    this._cartState = state;
  };
  get quoteId() {
    return (
      this.additionalDetails?.starlink_quote_request ||
      this.additionalDetails?.dia_quote_request ||
      this.additionalDetails?.bb_quote_request
    );
  }

  removeLocationsMissingServices = () => {
    const missingLocationIds = this.locationMissingServices.map(
      (loc) => loc.id,
    );

    this._locations = this._locations.filter(
      (loc) => !missingLocationIds.includes(loc.id),
    );

    if (missingLocationIds.includes(this.locationActiveId as string)) {
      this.locationActiveId = this._locations[0]?.id ?? null;
    }
  };

  itemsByLocation = (locationId: ICartItem['locationId']) => {
    return this.items.filter((item) => {
      return item.locationId === locationId;
    });
  };

  primaryItemsByLocation(locationId: ICartItem['locationId']) {
    return this.items.filter((item) => {
      return item.locationId === locationId && !item.item_details?.parentId;
    });
  }

  itemsByCategory(category: ICartItem['category']) {
    return filterItemsByCategory(this.items, category);
  }

  setLoadingDraftQuote = (loadingDraftQuote: boolean) => {
    this.loadingDraftQuote = loadingDraftQuote;
    this.loadingDraftForPricing = loadingDraftQuote;
  };

  setDIAFlowActive = (value: boolean) => {
    this.diaFlowActive = value;
  };

  setStartPolling = (value: boolean) => {
    this.startPolling = value;
  };

  setLocationActiveId(locationId: CartSiteAddress['id'] | null) {
    this.locationActiveId = locationId;
  }

  setShowOptimizationModal = (val: boolean) => {
    this.showOptimizationModal = val;
  };

  setPriceChange = (val: boolean, id: string) => {
    this.priceChangeMap[id] = val;
  };

  get priceChange() {
    return this.priceChangeMap;
  }

  goNextLocation = () => {
    if (!this.locationActiveId || this._locations.length === 0) return;

    const currentIndex = this._locations.findIndex(
      (loc) => loc.id === this.locationActiveId,
    );

    if (currentIndex !== -1 && currentIndex < this._locations.length - 1) {
      this.locationActiveId = this._locations[currentIndex + 1].id;
    }
  };

  goPrevLocation = () => {
    if (!this.locationActiveId || this._locations.length === 0) return;

    const currentIndex = this._locations.findIndex(
      (loc) => loc.id === this.locationActiveId,
    );

    if (currentIndex > 0) {
      this.locationActiveId = this._locations[currentIndex - 1].id;
    }
  };

  updateStateFromDraft = (draft: DraftOrderAPIResponse) => {
    const _locations: Array<CartSiteAddress> = [];
    const _items: Array<ICartItem> = [];

    (draft?.locations || []).forEach((location) => {
      _locations.push({
        ...location.address,
        external_id: location.id,
        address_line_1: location.address?.address_line_1 || '',
        city: location.address?.city || '',
        name: location.address?.name || '',
        state: location.address?.state || '',
        zip: location.address?.zip || '',
        id: location?.id || '',
      });
      location.order_items.forEach((item) =>
        _items.push({
          ...item,
          id: item.id,
          category: item.type === 'access_service' ? 'Access' : item.type,
          name: getAccessProductName(item.item_details),
          price: item.price as number,
          quantity: item.quantity,
          outOfStock: false,
          locationId: location.id,
          item_details: item.item_details,
        }),
      );
    });

    (draft?.order_items ?? []).forEach((item) => {
      _items.push({
        ...item,
        id: item.id,
        category: 'Mobility',
        subCategory: item.subtype,
        name: item.item_details?.name ?? item.item_details?.graniteId ?? '',
        price: item.price as number,
        quantity: item.quantity,
        outOfStock: false,
        locationId: null,
        item_details: item.item_details,
      });
    });

    // update store one time !!!
    this.additionalDetails = draft.additional_details ?? null;

    this._locations = _locations;

    this.items = _items;

    this.draftId = draft.id;

    this.locationActiveId = _locations[0]?.id ?? null;

    if (_items.length && !window.location.pathname.includes('v3/product')) {
      this._isOpen = true;
    }

    if (window.location.pathname.includes('access/product/All')) {
      return;
    }
    if (!this.selectedAccessProductCategory)
      this.selectedAccessProductCategory = draft.additional_details
        ?.starlink_quote_request
        ? 'Starlink'
        : draft.additional_details?.bb_quote_request
          ? 'Broadband'
          : draft.additional_details?.dia_quote_request
            ? 'DIA'
            : undefined;
  };

  getQuoteIdByCategory = (category: ProductType['category']) => {
    if (category === 'Starlink') {
      return this.additionalDetails?.starlink_quote_request;
    } else if (category === 'DIA') {
      return this.additionalDetails?.dia_quote_request;
    } else if (category === 'Broadband') {
      return this.additionalDetails?.bb_quote_request;
    }
    return null;
  };

  updateLocationOrderItems = flow(function* (
    this: CartStore,
    item: AccessProductType,
    cartItem: ICartItem,
  ) {
    const offeringId = item.id;
    try {
      const items = {
        order_items: this.items
          .filter((orderItem) => orderItem.locationId === this.locationActiveId)
          .map((orderItem) => {
            return {
              ...(orderItem.item_details?.offering_id ===
              cartItem.item_details?.offering_id
                ? {
                    ...cartItem,
                    item_details: {
                      ...item,
                      offering_id: offeringId,
                    },
                  }
                : orderItem),

              type: 'access_service',
              location_id: this.locationActiveId as string,
              cw_ticket_id: cartItem.cw_ticket_id,
              sf_id: cartItem.sf_id,
              cornerstone_id: cartItem.cornerstone_id,
              status: cartItem.status,
            };
          }),
      };
      yield addOrderItems(items, this.locationActiveId as string);
    } catch {
      console.error('');
    }
  });

  deleteProductFromOrder = flow(function* (
    this: CartStore,
    matchFn: (item: ICartItem) => boolean,
    removedItem: ICartItem,
    locationId?: string | number,
  ) {
    try {
      const items = {
        order_items: this.items
          .filter((orderItem) => orderItem.locationId === locationId)
          .map((orderItem) => {
            if (!matchFn(orderItem)) {
              return {
                ...orderItem,
                type: 'access_service',
                subtype: orderItem.subtype,
                location_id: locationId as string,
              };
            }
            return null;
          })
          .filter(Boolean),
      };
      //@ts-expect-error err
      return yield addOrderItems(items, locationId as string);
    } catch {
      this.items = [...this.items, removedItem];
    }
  });

  clearOrderItems = flow(function* (this: CartStore) {
    try {
      return yield clearAllOrderItems(this.draftId!);
    } catch {
      console.error('');
    }
  });

  reset = () => {
    this._isOpen = false;
    this._isEditingLocations = false;
    this._locations = [];
    this.loadingDraftQuote = false;
    this.draftId = null;
    this.additionalDetails = null;
    this.locationActiveId = null;
    this.items = [];
    this.selectedAccessProductCategory = null;
    this.deletedItems = [];
  };

  setLocations(newLocations: Array<CartSiteAddress>) {
    if (this._isEditingLocations) {
      this._stagedLocations = newLocations;
    } else {
      this._locations = newLocations;
    }
  }

  setConfigs = (configs: ConfigRequirements) => {
    this.additionalDetails = {
      ...this.additionalDetails,
      configs,
    };
  };

  setOrderItems(orderItems: Array<ICartItem>) {
    this.items = orderItems;
  }

  editLocations() {
    this._isEditingLocations = true;
    this._stagedLocations = [...this._locations];
  }

  cancelEditingLocations() {
    this._isEditingLocations = false;
    this._stagedLocations = [];
  }

  doneEditingLocations = () => {
    this._locations = [...this._stagedLocations];
    this._isEditingLocations = false;
    this._stagedLocations = [];
  };

  open() {
    this._isOpen = true;
  }

  close() {
    // cancle change if user click outside sidebar cart when is editing locations
    this.cancelEditingLocations();
    this._isOpen = false;
    this.resetShippingAssignmentMethod();
    setTimeout(() => {
      this._cartState = 'default';
    }, 400);
  }

  selectAccessProductCategory = (productCategory: ProductType['category']) => {
    this.open();
    this._cartState = 'locations';
    this.selectedAccessProductCategory = productCategory;
  };

  checkout() {}

  removeItem(id: ICartItem['id']) {
    this.items = this.items.filter((item) => item.id !== id);
  }

  async updateItemQuantity(id: ICartItem['id'], newQuantity: number) {
    if (newQuantity < 1 || !id) return;

    const primaryItem = this.items.find((item) => item.id === id);
    if (!primaryItem) return;

    // --- 1. Optimistic local update ---
    this.items = this.items.map((item) => {
      if (item.id === id) {
        return { ...item, quantity: newQuantity };
      }
      if (
        item.item_details?.parentId === primaryItem.item_details?.referenceId
      ) {
        return { ...item, quantity: newQuantity };
      }
      return item;
    });

    // --- 2. Cancel previous request ---
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Cancelled due to new quantity update');
    }
    cancelTokenSource = axios.CancelToken.source();

    // --- 3. Collect all affected items (primary + children) ---
    const primaryRefId = primaryItem.item_details?.referenceId;
    const itemsToUpdate = this.items.filter(
      (item) => item.id === id || item.item_details?.parentId === primaryRefId,
    );

    try {
      if (primaryItem.locationId) {
        // ========== ASSIGNED ITEMS ==========
        // Rebuild full draft for assigned locations
        const dataForPostOrUpdate = {
          additional_details: this.additionalDetails ?? {},
          locations: this.locations.map((location) => ({
            address: { ...location, id: location.id as string },
            order_items: this.itemsByLocation(location.id).map(
              buildItemPayload,
            ),
            id: location.id as string,
          })),
        };

        const draft = await updateOrder(dataForPostOrUpdate, this.draftId!, {
          cancelToken: cancelTokenSource.token,
        });

        this.updateStateFromDraft(draft);
      } else {
        // ========== UNASSIGNED ITEMS ==========
        // Update one by one
        const responses = await Promise.all(
          itemsToUpdate.map((item) =>
            updateOrderItem(
              item.id as string,
              {
                ...buildItemPayload(item),
                quantity: newQuantity,
              },
              {
                cancelToken: cancelTokenSource.token,
              },
            ),
          ),
        );

        const lastResponse = responses[responses.length - 1];
        if (lastResponse) {
          this.updateStateFromDraft(lastResponse);
        }
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Quantity update request cancelled');
      } else {
        console.error('Failed to update quantity', err);
      }
    }
  }

  updateStarlinkItemQuantity = (item: ICartItem, newQuantity: number) => {
    if (newQuantity < 1) return;

    const isMatchingItem = this.isMatchingStarlinkItem(
      item.item_details as ItemDetails,
    );

    this.items = this.items.map((cartItem) =>
      isMatchingItem(cartItem)
        ? { ...cartItem, quantity: newQuantity }
        : cartItem,
    );

    const updatedItem = this.items.find(isMatchingItem);

    if (updatedItem) {
      this.updateLocationOrderItems(
        item.item_details as AccessProductType,
        updatedItem,
      );
    }
  };

  updateItem = async (id: string | number, item: ICartItem) => {
    this.open();
    const quantity =
      this.items.find((cartItem) => cartItem.id === id)?.quantity || 1;
    this.items = this.items.filter((cartItem) => {
      if (cartItem.item_details?.pimProductPricingId !== undefined) {
        return (
          String(cartItem.item_details?.pimProductPricingId) !== String(id)
        );
      }
      return (
        String(cartItem.item_details?.pim_product_pricing_id) !== String(id)
      );
    });
    this.items = [...this.items, { ...item, quantity }];
  };

  addItem = flow(function* (this: CartStore, ...items: ICartItem[]) {
    this.open();

    if (!items.length) return;

    const countById = (arr: ICartItem[]) => {
      const map = new Map<string, number>();
      for (const item of arr) {
        const id = JSON.stringify({
          pricingId:
            item.item_details?.pimProductPricingId ??
            item.item_details?.pim_product_pricing_id,
          carrierName: item.item_details?.carrierName,
          additionalCarrierName: item.item_details?.additionalCarrierName,
        });
        map.set(id, (map.get(id) ?? 0) + 1);
      }
      return map;
    };

    const sameCounts = (a: Map<string, number>, b: Map<string, number>) => {
      if (a.size !== b.size) return false;
      for (const [id, count] of a.entries()) {
        if (b.get(id) !== count) return false;
      }
      return true;
    };

    const findSameBundleMainItem = (items: ICartItem[]) => {
      const itemsCounts = countById(items);

      // find all main items in the cart
      const mains = this.items.filter((ci) => !ci.item_details?.parentId);

      for (const main of mains) {
        // collect the bundle for this main
        const bundle = [
          main,
          ...this.items.filter(
            (ci) =>
              ci.item_details?.parentId === main.item_details?.referenceId,
          ),
        ];

        // compare sets including item counts
        const bundleCounts = countById(bundle);
        if (sameCounts(itemsCounts, bundleCounts)) {
          return main; // found an identical bundle
        }
      }

      return null;
    };

    const existingMainItem = findSameBundleMainItem(items);

    if (existingMainItem) {
      this.updateItemQuantity(
        existingMainItem.id!,
        existingMainItem.quantity + items[0].quantity,
      );
      return;
    }

    this.items = [...this.items, ...items];

    const responses: DraftOrderAPIResponse[] = [];
    for (const item of items) {
      const payload: CreateOrderItem = {
        type: 'Mobility',
        subtype: item.subtype,
        quantity: item.quantity,
        price: item.price,
        item_details: { ...item.item_details },
      };

      // call API with yield
      const response = yield createOrderItem(this.draftId!, payload);
      responses.push(response);
    }

    // update with the last response
    yield this.updateStateFromDraft(responses.at(-1)!);
  });

  getTotal() {
    const total = this.items.reduce((sum, item) => {
      const itemDetails = item.item_details as AccessProductType;

      // Check for parentId property in itemDetails
      if ('parentId' in itemDetails && itemDetails?.parentId) {
        return sum;
      }

      const pricing =
        itemDetails?.formal_broadband_pricing ??
        itemDetails?.formal_dia_pricing;

      const serviceActivation = Number(pricing?.service_activation ?? 0);

      return sum + (item.price + serviceActivation) * item.quantity;
    }, 0);

    return total;
  }

  hasCircuitLimitReached = (
    subCategory: ICartItem['subCategory'],
    offeringId: string,
  ): boolean => {
    const locId = this.locationActiveId;
    if (!locId) return false;

    const circuits = this.items.filter(
      (item) =>
        item.locationId === locId &&
        item.subCategory === subCategory &&
        // don't disable items buttons that are already added to cart
        item.item_details?.offering_id !== offeringId,
    );

    const uniqueCombos = new Set(
      circuits.map((item) => item.item_details?.provider),
    );

    return uniqueCombos.size >= 2;
  };

  getCircuitsByProductTypeAndLocation = (
    productType: ICartItem['category'],
    locationId: string | number,
  ) => {
    return this.items.filter(
      (item) =>
        item.subtype !== 'starlink' &&
        item.category === productType &&
        item.locationId === locationId,
    );
  };

  getTotalByLocation(locationId: ICartItem['locationId']) {
    const total = this.items
      .filter((item) => item.locationId === locationId)
      .reduce((sum, item) => {
        const itemDetails = item.item_details as AccessProductType;

        // Check for parentId property in itemDetails
        if ('parentId' in itemDetails && itemDetails?.parentId) {
          return sum;
        }

        const pricing =
          itemDetails?.formal_broadband_pricing ??
          itemDetails?.formal_dia_pricing;

        const serviceActivation = Number(pricing?.service_activation ?? 0);

        return sum + (item.price + serviceActivation) * item.quantity;
      }, 0);

    return total;
  }

  hasProviderCombo = (provider: string, locationId: string | number) => {
    return this.items.some(
      (item) =>
        item.locationId === locationId &&
        item.item_details?.provider === provider,
    );
  };

  canAddCircuitToLocation = (
    item: ICartItem,
    provider: string,
  ): {
    valid: boolean;
    reason?: string;
  } => {
    const locationId = item.locationId ?? this.locationActiveId;
    if (!locationId) return { valid: false };

    const circuits = this.getCircuitsByProductTypeAndLocation(
      item.category,
      locationId,
    );

    if (circuits.length >= 2) {
      return {
        valid: false,
        reason: "You may only add two access circuits to each location's cart.",
      };
    }

    const hasCombo = this.hasProviderCombo(provider ?? '', locationId);
    if (hasCombo) {
      return {
        valid: false,
        reason:
          'Access service with this vendor already added for this location!',
      };
    }

    return { valid: true };
  };

  isItemAlreadyInCart = (id: string): boolean => {
    return this.items.some(
      (item) =>
        item.locationId === this.locationActiveId &&
        item.item_details?.offering_id === id,
    );
  };

  isMatchingStarlinkItem =
    (product: ItemDetails, locationId?: string) => (item: ICartItem) => {
      const effectiveLocationId = locationId ?? this.locationActiveId;

      const matchDetails = {
        locationMatch: item.locationId === effectiveLocationId,
        offeringMatch: item.item_details?.offering_id === product.id,
        tierMatch:
          item.item_details?.service_level_tier === product.service_level_tier,
        bundleMatch: item.item_details?.bundle === product.bundle,
      };

      const match = Object.values(matchDetails).every(Boolean);

      return match;
    };

  isStarlinkItemAlreadyInCart = (product: ItemDetails): boolean => {
    return this.items.some(this.isMatchingStarlinkItem(product));
  };

  addStarlinkCartItem = async (
    item: AccessProductType & {
      isOtherOption?: boolean;
      bundle?: string;
      graniteInstall?: boolean;
    },
  ): Promise<{ success: boolean; reason?: string }> => {
    const locationId = this.locationActiveId;
    if (!locationId) return { success: false };
    const cartItem = {
      category: 'Access',
      subtype: item.type!,
      name: getAccessProductName(item),
      price: item.formal_starlink_pricing?.total_mrc ?? 0,
      quantity: 1,
      outOfStock: false,
      locationId: this.locationActiveId,
      item_details: {
        ...item,
        bundle: item.bundle,
        offering_id: item.id,
        isOtherOption: item.isOtherOption,
        graniteInstall: item.graniteInstall,
      },
    };

    const newItem = { ...cartItem, locationId };
    this.items = [...this.items, newItem];
    this.open();
    await this.updateLocationOrderItems(item, cartItem);
    return { success: true };
  };

  removeStarlinkCartItem = async (
    product: ItemDetails,
    locationId?: string,
  ) => {
    const effectiveLocationId = locationId ?? this.locationActiveId;

    if (!effectiveLocationId) return { success: false };

    const matcher = this.isMatchingStarlinkItem(
      product,
      effectiveLocationId as string,
    );
    const itemIndex = this.items.findIndex(matcher);

    if (itemIndex === -1) {
      return { success: false };
    }
    const removedItem = this.items[itemIndex];
    this.items = this.items.filter((_, index) => index !== itemIndex);

    await this.deleteProductFromOrder(
      matcher,
      removedItem,
      effectiveLocationId as string,
    );
  };

  private enrichAccessItemPricing = (
    item: AccessProductType & Partial<ItemDetails>,
  ): AccessProductType & Partial<ItemDetails> => {
    const pricingPath = `formal_${item.type}_pricing`;
    const pricing = item?.[pricingPath as keyof typeof item] || {};

    const totalMRCWithoutMNS = getOfferingPrice(item, true);
    const totalMRC = totalMRCWithoutMNS + getMNSPrice(item);

    return {
      ...item,
      [pricingPath]: {
        ...pricing,
        total_mrc: totalMRC,
      },
    };
  };

  private handleMnsOptimization = async (
    accessItems: ICartItem[],
  ): Promise<void> => {
    this.deletedItems = [...accessItems];

    const [first, second] = accessItems;

    const enhancedItemDetails = {
      ...this.enrichAccessItemPricing({
        ...first.item_details,
        mns_essential: false,
        mns_enhanced: true,
      } as AccessProductType),
    };

    const enhancedItem: ICartItem = {
      ...first,
      item_details: {
        ...enhancedItemDetails,
        price: getOfferingPrice(enhancedItemDetails, true),
        total_price: getOfferingPrice(enhancedItemDetails, true),
      },
      price:
        getOfferingPrice(enhancedItemDetails, true) +
        getMNSPrice(enhancedItemDetails),
    };

    const cleanedItemDetails = {
      ...this.enrichAccessItemPricing({
        ...second.item_details,
        mns_essential: false,
        mns_enhanced: false,
      } as AccessProductType),
    };

    const cleanedSecond: ICartItem = {
      ...second,
      item_details: {
        ...cleanedItemDetails,
        price: getOfferingPrice(cleanedItemDetails, true),
        total_price: getOfferingPrice(cleanedItemDetails, true),
      },
      price: getOfferingPrice(second.item_details as AccessProductType, true),
    };

    this.deletedItems = [enhancedItem, cleanedSecond];
    this.setShowOptimizationModal(true);
  };

  addAccessCartItem = async (
    item: AccessProductType & {
      isOtherOption?: boolean;
      mns_essential?: boolean;
      mns_enhanced?: boolean;
    },
  ): Promise<{ success: boolean; reason?: string }> => {
    const locationId = this.locationActiveId;
    if (!locationId) return { success: false };

    const pricingPath = `formal_${item.type}_pricing`;
    const pricing = item?.[pricingPath as keyof typeof item] || {};

    const totalMRCWithoutMNS = getOfferingPrice(item);
    const totalMRC = totalMRCWithoutMNS + getMNSPrice(item);

    item = {
      ...item,
      [pricingPath]: {
        ...pricing,
        total_mrc_without_mns: totalMRCWithoutMNS,
        total_mrc: totalMRC,
      },
    };

    const cartItem = {
      category: 'Access',
      subtype: item.type!,
      name: getAccessProductName(item),
      price: totalMRC,
      quantity: 1,
      outOfStock: false,
      locationId: this.locationActiveId,
      item_details: {
        ...item,
        provider: item.provider,
        offering_id: item.id,
        isOtherOption: item.isOtherOption,
        mns_essential: item.mns_essential,
        mns_enhanced: item.mns_enhanced,
      },
    };

    const validation = this.canAddCircuitToLocation(cartItem, item.provider!);

    if (!validation.valid) {
      showToast.error({ message: validation.reason ?? '', autoClose: true });
      // Don't add item
      return { success: false, reason: validation.reason };
    }

    const newItem = { ...cartItem, locationId };
    this.items = [...this.items, newItem];
    this.open();

    const accessItems = this.getCircuitsByProductTypeAndLocation(
      'Access',
      locationId,
    );

    const mnsEssentials = accessItems.filter(
      (i) => i.item_details?.mns_essential,
    );

    if (mnsEssentials.length === 2) {
      await this.handleMnsOptimization(accessItems);
    } else {
      await this.updateLocationOrderItems(item, cartItem);
    }

    return { success: true };
  };

  keepMnsOptimization = async () => {
    const locationId = this.locationActiveId;
    if (!locationId || this.deletedItems.length !== 2) return;

    const [enhancedItem, cleanedSecond] = this.deletedItems;

    this.items = [
      ...this.items.filter((item) => item.locationId !== locationId),
      enhancedItem,
      cleanedSecond,
    ];

    await this.updateLocationOrderItems(
      enhancedItem.item_details as AccessProductType,
      enhancedItem,
    );
    await this.updateLocationOrderItems(
      cleanedSecond.item_details as AccessProductType,
      cleanedSecond,
    );

    this.setShowOptimizationModal(false);

    [enhancedItem, cleanedSecond].forEach((item) => {
      if (item.item_details && typeof item.item_details.id === 'string') {
        const itemId = item.item_details.id;
        this.setPriceChange(true, itemId);

        setTimeout(() => {
          this.setPriceChange(false, itemId);
        }, 3000);
      }
    });

    this.deletedItems = [];
  };

  revertMnsOptimization = async () => {
    const locationId = this.locationActiveId;
    if (!locationId || !this.deletedItems.length) return;

    const revertedItems = this.deletedItems.map((item) => {
      const revertedItemDetails = {
        ...this.enrichAccessItemPricing({
          ...item.item_details,
          mns_essential: true,
          mns_enhanced: false,
        } as AccessProductType),
      };
      return {
        ...item,
        item_details: {
          ...revertedItemDetails,
          price: getOfferingPrice(revertedItemDetails, true),
          total_price: getOfferingPrice(revertedItemDetails, true),
        },
        price:
          getOfferingPrice(revertedItemDetails, true) +
          getMNSPrice(revertedItemDetails),
      };
    });

    const itemsToSend = revertedItems.map((item) => ({
      ...item,
      type: 'access_service',
      location_id: locationId as string,
    }));

    await addOrderItems({ order_items: itemsToSend }, locationId as string);

    this.items = [
      ...this.items.filter((item) => item.locationId !== locationId),
      ...revertedItems,
    ];
    this.deletedItems = [];
    this.setShowOptimizationModal(false);
  };

  removeAccessCartItem = async (id: string, locationId?: string) => {
    const effectiveLocationId = locationId ?? this.locationActiveId;
    if (!effectiveLocationId) return { success: false };

    const itemIndex = this.items.findIndex(
      (item) => item.item_details?.offering_id === id,
    );
    if (itemIndex === -1) {
      return { success: false };
    }
    const removedItem = this.items[itemIndex];
    // Optimistically remove the item from the cart
    this.items = this.items.filter(
      (item) => item.item_details?.offering_id !== id,
    );

    await this.deleteProductFromOrder(
      (item) => item.item_details?.offering_id === id,
      removedItem,
      effectiveLocationId,
    );
  };

  deleteUnassignedItem = async (id: string) => {
    const itemIndex = this.items.findIndex(
      (item) => item.id === id && !item.locationId,
    );

    if (itemIndex === -1) {
      return { success: false };
    }

    const originalItems = [...this.items];
    const [removedItem] = this.items.splice(itemIndex, 1); // Optimistic removal

    try {
      await this.deleteProductFromOrder(
        (item) => item.item_details?.offering_id === id && !item.locationId,
        removedItem,
      );
      return { success: true };
    } catch (error) {
      console.error('Error deleting unassigned item:', error);
      this.items = originalItems; // Rollback
      return { success: false, reason: 'Failed to delete unassigned item' };
    }
  };

  addUnassignedItemBack = async (item: ICartItem) => {
    const item_ = this.items.find((i) => i.id === item.id);
    if (item_?.item_details) {
      item_.item_details.deleted = false;
    }
    const dataForPostOrUpdate: Pick<PostDraftRequest, 'order_items'> = {
      order_items: this.items
        .filter((i) => i.id === item.id || !i.item_details?.deleted)
        .map((i) => ({
          type: i.category ?? '',
          subtype: i.subtype,
          pim_price_id:
            (i.item_details?.pim_price_id as string | null) ??
            (i.item_details?.pim_product_pricing_id as unknown as
              | string
              | null) ??
            null,
          price: i.price ?? null,
          quantity: i.quantity,
          location_id: String(i.locationId ?? ''),
          item_details: i.item_details,
        })),
    };

    await updateOrder(dataForPostOrUpdate, this.draftId!);
  };

  removeMobilityCartItem = flow(function* (
    this: CartStore,
    primaryId: string,
    undo?: boolean,
  ) {
    const primaryItemIndex = this.items.findIndex(
      (item) => item.id === primaryId,
    );
    const primaryItem = this.items[primaryItemIndex];

    const idsToDelete = [
      primaryId, // primary item ID
      ...this.items
        .filter(
          (item) =>
            item.item_details?.parentId ===
            primaryItem?.item_details?.referenceId,
        )
        .map((item) => item.id) // child items IDs
        .filter(Boolean),
    ];

    if (!undo) {
      // const [removedItem] =
      this.items.splice(primaryItemIndex, 1); // Optimistic removal
    } else {
      if (primaryItem.item_details) {
        primaryItem.item_details.deleted = true;
      }
    }

    try {
      const responses = yield Promise.all(
        idsToDelete.map((id) => deleteOrderItem(this.draftId!, String(id))),
      );
      // const updatedDraft =
      responses[responses.length - 1];
      // if (updatedDraft) {
      //   yield this.updateStateFromDraft(updatedDraft);
      // }
    } catch (error) {
      console.error('Error removing mobility cart item:', error);
    }
  });

  resetOrderItems = async () => {
    this.items = this.deletedItems;
    this.deletedItems = [];

    if (cancelTokenSource) {
      cancelTokenSource?.cancel('Operation canceled due to new request.');
    }
    cancelTokenSource = axios.CancelToken.source();

    const dataForPostOrUpdate = {
      additional_details: this.additionalDetails ?? {},
      locations: this.locations.map((location) => {
        const order_items = this.itemsByLocation(location.id).map(
          buildItemPayload,
        );
        return {
          address: { ...location, id: location.id as string },
          order_items,
          id: location.id as string,
        };
      }),
      order_items: this.unnassignedItems.map(buildItemPayload),
    };

    const draft = await updateOrder(dataForPostOrUpdate, this.draftId!, {
      cancelToken: cancelTokenSource.token,
    });
    this.updateStateFromDraft(draft);
  };

  clearAll = async () => {
    let undoClicked = false;
    this.deletedItems = [...this.items];
    this.items = [];

    if (cancelTokenSource) {
      cancelTokenSource?.cancel('Operation canceled due to new request.');
    }
    cancelTokenSource = axios.CancelToken.source();
    const dataForPostOrUpdate = {
      additional_details: this.additionalDetails ?? {},
      locations: this.locations.map((location) => ({
        address: { ...location, id: location.id as string },
        order_items: [],
        id: location.id as string,
      })),
      order_items: [],
    };
    const draft = await updateOrder(dataForPostOrUpdate, this.draftId!, {
      cancelToken: cancelTokenSource.token,
    });
    this.updateStateFromDraft(draft);

    const toastId = showToast.confirmation({
      message: 'Cart cleared!',
      buttonLabel: 'Undo',
      onClick: () => {
        undoClicked = true;
        this.resetOrderItems();
        toast.dismiss(toastId);
      },
    });

    toast.onChange(async (payload) => {
      if (
        payload.status === 'removed' &&
        payload.id === toastId &&
        !undoClicked
      ) {
        this.loadingDraftQuote = true;
        await this.clearOrderItems();
        this.loadingDraftQuote = false;
      }
    });
  };

  setShippingAssignmentMethod(method: ShippingAssignmentMethod) {
    this.shippingAssignmentMethod = method;
  }

  resetShippingAssignmentMethod() {
    this.shippingAssignmentMethod = 'unset';
    this.unassginedItemsAreAssigned = false;
    this.items = this.items.filter((item) => !item.item_details?.delete);
  }
}

const CartContext = createContext<CartStore | null>(null);

export const CartContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const store = useMemo(() => new CartStore(), []);
  const { resumedOrderId } = useParams();

  const _getDraftOrder = async () => {
    if (resumedOrderId) {
      store.updateResumeOrder({ id: resumedOrderId });
      return getOrderDetails(resumedOrderId);
    } else {
      store.clearResumeOrder();
      return getLastDraftOrder();
    }
  };

  useQuery([resumedOrderId], _getDraftOrder, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (resumedOrderId)
        store.updateResumeOrder({ orderName: data?.order_name });
      if (data) {
        if (data.locations?.length)
          store.setLocationActiveId(data.locations[0].id);
        store.updateStateFromDraft(data);
      }
    },
    onSettled: () => {
      store.setLoadingDraftQuote(false);
    },
  });

  return <CartContext.Provider value={store}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartStore must be used within a CartProvider');
  }
  return context;
};
